import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { xor } from 'lodash';
import { message } from 'antd';

import { useCloseModal } from '@core/router';
import { GroupedOperations, useAuth } from '@modules/auth';
import { useService } from '@core/inversify-react';
import { GetOperationsRepoType, GetUserPermissionsRepoType, IGetOperationsRepo, IGetUserPermissionsRepo, IUpdateUserPermissionsRepo, UpdateUserPermissionsRepoType, useUserById } from '@modules/users';
import { useBranchesWithDeliveryPoints } from '@modules/branches/hooks/use-branches-with-delivery-points';
import { useBranchPartners } from '@modules/branch-partners/hooks';
import { useCashRegisters } from '@modules/cash-flow/cash-registers';
export const useUpdateUserPermissions = () => {
  const close = useCloseModal();
  const params = useParams<{ id: string }>();
  const auth = useAuth();
  const user = useUserById(params.id);

  const cashRegisters = useCashRegisters();
  const companies = useBranchPartners();
  const branchList = useBranchesWithDeliveryPoints();

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [operationIds, setOperationIds] = useState<number[]>([]);
  const [cashRegisterId, setCashRegisterId] = useState<number>();
  const [adminBranchId, setAdminBranchId] = useState<number>();
  const [companyId, setCompanyId] = useState<number>();
  const getOperationsRepo = useService<IGetOperationsRepo>(GetOperationsRepoType);
  const getUserPermissions = useService<IGetUserPermissionsRepo>(GetUserPermissionsRepoType);
  const updateUserPermissions = useService<IUpdateUserPermissionsRepo>(UpdateUserPermissionsRepoType);

  const isSelf = useMemo(() => auth.user.data?.id.toString() === params.id, [params.id, auth.user.data?.id]);

  const branches = useMemo(() => {
    return branchList.data?.filter((branch) => branch.companyId === companyId);
  }, [branchList.data, companyId]);

  const onClose = useCallback(() => close('/@next/users'), [close]);

  const operations = useQuery<GroupedOperations[], Error, GroupedOperations[]>(['operations'], async () => {
    const result = await getOperationsRepo.execute({ page: 1, per_page: 200 });

    if (result.status === 200) {
      return result.response;
    } else {
      throw new Error(result.response);
    }
  });

  const permissions = useQuery<number[], Error, number[], ['users', string, 'permissions']>(['users', params.id, 'permissions'], async ({ queryKey }) => {
    const [, userId] = queryKey;

    const result = await getUserPermissions.execute({ userId });

    if (result.status === 200) {
      setCompanyId(result.response.company_id);
      return result.response.permissions;
    } else {
      throw new Error(result.response);
    }
  });

  const onSwitchChange = useCallback(
    (value: number) => {
      const flatOperations = operations.data?.map(({ operations }) => operations).flat() || [];
      const flatOperationIds = flatOperations.map(({ id }) => id);
      const changeUserPermissionsOperationId = flatOperations.find(({ codeName }) => codeName === 'changeuserpermissions')?.id;

      if (changeUserPermissionsOperationId === value && !operationIds.includes(value)) {
        setOperationIds(flatOperationIds);
      } else {
        setOperationIds((operationIds) => xor<number>(operationIds, [value]));
      }
    },
    [operationIds, operations.data],
  );

  const onCashRegisterIdChange = useCallback((value: number | undefined) => {
    setCashRegisterId(value);
  }, []);

  const onAdminBranchIdChange = useCallback((value: number | undefined) => {
    setAdminBranchId(value);
  }, []);

  const onCompanyIdChange = useCallback((value: number | undefined) => {
    setCompanyId(value);
  }, []);

  const onSubmit = useCallback(async () => {
    setSubmitting(true);

    const result = await updateUserPermissions.execute({ userId: params.id, operationIds, cashRegisterId, adminBranchId });

    if (result.status === 200) {
      onClose();
    } else {
      message.error(result.response);
    }

    setSubmitting(false);
  }, [adminBranchId, cashRegisterId, onClose, operationIds, params.id, updateUserPermissions]);

  useEffect(() => {
    if (permissions.data) {
      setOperationIds(permissions.data);
    }
  }, [permissions.data]);

  useEffect(() => {
    if (user.data?.cashRegisterId) {
      setCashRegisterId(user.data.cashRegisterId);
    }
  }, [user.data?.cashRegisterId]);

  useEffect(() => {
    if (user.data?.adminBranchId) {
      setAdminBranchId(user.data.adminBranchId);
    }
  }, [user.data?.adminBranchId]);

  return {
    operations,
    permissions,
    onSubmit,
    onClose,
    submitting,
    isSelf,
    onSwitchChange,
    operationIds,
    cashRegisterId,
    adminBranchId,
    onCashRegisterIdChange,
    onAdminBranchIdChange,
    onCompanyIdChange,
    cashRegisters,
    companies,
    branches,
    companyId,
  };
};
